import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { adminAPI } from '@/lib/api/admin'

interface MediaFile {
  id: string
  file: File
  preview: string
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'error'
  uploadProgress?: number
  mediaId?: string
  error?: string
}

interface MediaUploadZoneProps {
  onMediaUploaded: (mediaIds: string[]) => void
  maxFiles?: number
  acceptedFileTypes?: Record<string, string[]>
}

export default function MediaUploadZone({
  onMediaUploaded,
  maxFiles = 10,
  acceptedFileTypes = {
    'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    'video/*': ['.mp4', '.mov', '.avi']
  }
}: MediaUploadZoneProps) {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.slice(0, maxFiles - mediaFiles.length).map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      uploadStatus: 'pending' as const
    }))

    setMediaFiles(prev => [...prev, ...newFiles])
    uploadFiles(newFiles)
  }, [mediaFiles.length, maxFiles])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: maxFiles - mediaFiles.length,
    disabled: isUploading || mediaFiles.length >= maxFiles
  })

  const uploadFiles = async (filesToUpload: MediaFile[]) => {
    setIsUploading(true)
    
    try {
      const uploadPromises = filesToUpload.map(async (mediaFile) => {
        try {
          // Update status to uploading
          setMediaFiles(prev => prev.map(f => 
            f.id === mediaFile.id 
              ? { ...f, uploadStatus: 'uploading', uploadProgress: 0 }
              : f
          ))

          // Upload the file
          const response = await adminAPI.uploadMedia(mediaFile.file)
          
          // Update status to completed
          setMediaFiles(prev => prev.map(f => 
            f.id === mediaFile.id 
              ? { ...f, uploadStatus: 'completed', uploadProgress: 100, mediaId: response.mediaId }
              : f
          ))

          return response.mediaId
        } catch (error) {
          // Update status to error
          setMediaFiles(prev => prev.map(f => 
            f.id === mediaFile.id 
              ? { ...f, uploadStatus: 'error', error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          ))
          return null
        }
      })

      const results = await Promise.all(uploadPromises)
      const successfulUploads = results.filter((id): id is string => id !== null)
      
      // Notify parent component of successful uploads
      if (successfulUploads.length > 0) {
        const allMediaIds = mediaFiles
          .filter(f => f.uploadStatus === 'completed' && f.mediaId)
          .map(f => f.mediaId!)
          .concat(successfulUploads)
        
        onMediaUploaded(allMediaIds)
      }
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const removeFile = (fileId: string) => {
    setMediaFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId)
      const mediaIds = updated
        .filter(f => f.uploadStatus === 'completed' && f.mediaId)
        .map(f => f.mediaId!)
      onMediaUploaded(mediaIds)
      return updated
    })
  }

  const moveFile = (fromIndex: number, toIndex: number) => {
    setMediaFiles(prev => {
      const updated = [...prev]
      const [movedFile] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, movedFile)
      
      const mediaIds = updated
        .filter(f => f.uploadStatus === 'completed' && f.mediaId)
        .map(f => f.mediaId!)
      onMediaUploaded(mediaIds)
      return updated
    })
  }

  const retryUpload = (fileId: string) => {
    const fileToRetry = mediaFiles.find(f => f.id === fileId)
    if (fileToRetry && fileToRetry.uploadStatus === 'error') {
      uploadFiles([fileToRetry])
    }
  }

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          isDragActive
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-300 hover:border-purple-400'
        } ${
          isUploading || mediaFiles.length >= maxFiles
            ? 'opacity-50 cursor-not-allowed'
            : ''
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center">
          <svg
            className="w-12 h-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          {isDragActive ? (
            <p className="text-lg text-purple-600">Drop the files here...</p>
          ) : (
            <div>
              <p className="text-lg text-gray-600 mb-2">
                Drag & drop media files here, or click to select
              </p>
              <p className="text-sm text-gray-500">
                Images (JPEG, PNG, WebP) and Videos (MP4, MOV, AVI)
              </p>
              <p className="text-sm text-gray-500">
                Max {maxFiles} files • {mediaFiles.length}/{maxFiles} uploaded
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File Previews */}
      {mediaFiles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {mediaFiles.map((mediaFile, index) => (
            <MediaPreview
              key={mediaFile.id}
              mediaFile={mediaFile}
              index={index}
              onRemove={() => removeFile(mediaFile.id)}
              onMove={moveFile}
              onRetry={() => retryUpload(mediaFile.id)}
              canMove={mediaFiles.length > 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface MediaPreviewProps {
  mediaFile: MediaFile
  index: number
  onRemove: () => void
  onMove: (fromIndex: number, toIndex: number) => void
  onRetry: () => void
  canMove: boolean
}

function MediaPreview({ 
  mediaFile, 
  index, 
  onRemove, 
  onMove, 
  onRetry, 
  canMove 
}: MediaPreviewProps) {
  const isVideo = mediaFile.file.type.startsWith('video/')

  return (
    <div className="relative group bg-white border rounded-lg overflow-hidden">
      {/* Media Preview */}
      <div className="aspect-square bg-gray-100">
        {isVideo ? (
          <video
            src={mediaFile.preview}
            className="w-full h-full object-cover"
            muted
          />
        ) : (
          <img
            src={mediaFile.preview}
            alt={mediaFile.file.name}
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Upload Status Overlay */}
        {mediaFile.uploadStatus === 'uploading' && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm">Uploading...</p>
              {mediaFile.uploadProgress && (
                <p className="text-xs">{mediaFile.uploadProgress}%</p>
              )}
            </div>
          </div>
        )}
        
        {mediaFile.uploadStatus === 'error' && (
          <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
            <div className="text-white text-center">
              <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-xs">Failed</p>
            </div>
          </div>
        )}
        
        {mediaFile.uploadStatus === 'completed' && (
          <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      
      {/* File Info */}
      <div className="p-2">
        <p className="text-xs text-gray-600 truncate" title={mediaFile.file.name}>
          {mediaFile.file.name}
        </p>
        <p className="text-xs text-gray-500">
          {(mediaFile.file.size / 1024 / 1024).toFixed(1)} MB
        </p>
      </div>
      
      {/* Action Buttons */}
      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex space-x-1">
          {/* Move buttons */}
          {canMove && (
            <>
              {index > 0 && (
                <button
                  onClick={() => onMove(index, index - 1)}
                  className="w-6 h-6 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-75 flex items-center justify-center"
                  title="Move left"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => onMove(index, index + 1)}
                className="w-6 h-6 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-75 flex items-center justify-center"
                title="Move right"
                disabled={index === 0} // This would need proper logic for last item
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Remove/Retry Button */}
      <button
        onClick={mediaFile.uploadStatus === 'error' ? onRetry : onRemove}
        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
          mediaFile.uploadStatus === 'error'
            ? 'bg-yellow-500 hover:bg-yellow-600'
            : 'bg-red-500 hover:bg-red-600'
        } text-white`}
        title={mediaFile.uploadStatus === 'error' ? 'Retry upload' : 'Remove file'}
      >
        {mediaFile.uploadStatus === 'error' ? (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      {/* Error message */}
      {mediaFile.uploadStatus === 'error' && mediaFile.error && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs p-1">
          {mediaFile.error}
        </div>
      )}
    </div>
  )
}
